/**
 * «Dati della pratica» del canale di sistema «Documento Unico» (27/10/2026).
 *
 * Il canale chiede tre campi FACOLTATIVI all'apertura del ticket; le chiavi
 * sono fisse lato backend, l'admin ne rinomina solo le etichette. Qui stanno
 * le regole pure condivise dal form dell'associato, dal dettaglio
 * dell'associato e dalla card degli operatori (v3 e v4).
 *
 * Server di prima (senza `campiRichiesta`/`campiExtra`): ogni funzione
 * restituisce l'insieme vuoto e nessun riquadro compare.
 */
import type { CampiExtra, CampoRichiesta, TicketDetail } from '../../services/supportApi';

/** Etichette predefinite, nell'ordine fisso del backend. */
export const CAMPI_PREDEFINITI: CampoRichiesta[] = [
  { chiave: 'targa', etichetta: 'Targa', maxLength: 15 },
  { chiave: 'tipoFormalita', etichetta: 'Tipo formalità', maxLength: 120 },
  { chiave: 'numeroTicket', etichetta: 'Numero ticket ACI / DTT', maxLength: 60 },
];

/** Chiave mostrata in monospace (targa). */
export const CHIAVE_MONOSPACE = 'targa';

/**
 * Elenco dei campi arrivato dal server, ripulito: si scartano le voci senza
 * chiave; etichetta vuota = predefinita; `maxLength` assente = predefinito.
 */
export const campiRichiestaDi = (raw: unknown): CampoRichiesta[] => {
  if (!Array.isArray(raw)) return [];
  const out: CampoRichiesta[] = [];
  for (const c of raw) {
    if (!c || typeof c !== 'object') continue;
    const chiave = typeof (c as any).chiave === 'string' ? (c as any).chiave.trim() : '';
    if (!chiave || out.some((x) => x.chiave === chiave)) continue;
    const def = CAMPI_PREDEFINITI.find((d) => d.chiave === chiave);
    const etichetta = typeof (c as any).etichetta === 'string' && (c as any).etichetta.trim()
      ? (c as any).etichetta.trim()
      : def?.etichetta || chiave;
    const max = Number((c as any).maxLength);
    const maxLength = Number.isFinite(max) && max > 0 ? Math.floor(max) : def?.maxLength || 120;
    out.push({ chiave, etichetta, maxLength });
  }
  return out;
};

/**
 * Valori da inviare: solo le chiavi dei campi del canale, ripulite dagli
 * spazi ai bordi e tagliate al massimo; i vuoti si tolgono. `undefined` se
 * non resta nulla (la richiesta allora non porta `campiExtra`).
 */
export const valoriDaInviare = (
  campi: CampoRichiesta[],
  valori: Record<string, string | undefined>
): CampiExtra | undefined => {
  const out: CampiExtra = {};
  for (const c of campi) {
    const v = (valori[c.chiave] || '').trim().slice(0, c.maxLength);
    if (v) out[c.chiave] = v;
  }
  return Object.keys(out).length > 0 ? out : undefined;
};

/** Valori valorizzati (stringhe non vuote) di `campiExtra`. */
const valoriPresenti = (campiExtra: unknown): Record<string, string> => {
  const out: Record<string, string> = {};
  if (!campiExtra || typeof campiExtra !== 'object') return out;
  for (const [k, v] of Object.entries(campiExtra as Record<string, unknown>)) {
    if (typeof v === 'string' && v.trim()) out[k] = v;
  }
  return out;
};

export interface RigaDatiPratica {
  chiave: string;
  etichetta: string;
  /** Stringa vuota = campo previsto ma non compilato. */
  valore: string;
}

/**
 * Righe del riquadro «Dati della pratica» nel dettaglio.
 * - Canale con campi: tutti i campi con le etichette ATTUALI, anche vuoti.
 * - Canale senza campi (ticket trasferito altrove) ma valori salvati: solo i
 *   valori presenti, con le etichette predefinite.
 * - Altrimenti nessuna riga (= nessun riquadro).
 */
export const righeDatiPratica = (
  ticket: Pick<TicketDetail, 'campiExtra' | 'campiRichiesta'> | null | undefined
): RigaDatiPratica[] => {
  // I due campi sono facoltativi (server di prima): si leggono senza fidarsi della forma.
  const t = ticket || {};
  const campi = campiRichiestaDi(t.campiRichiesta);
  const valori = valoriPresenti(t.campiExtra);
  if (campi.length > 0) {
    return campi.map((c) => ({ chiave: c.chiave, etichetta: c.etichetta, valore: valori[c.chiave] || '' }));
  }
  const righe: RigaDatiPratica[] = CAMPI_PREDEFINITI
    .filter((d) => valori[d.chiave])
    .map((d) => ({ chiave: d.chiave, etichetta: d.etichetta, valore: valori[d.chiave] }));
  // Chiavi sconosciute (backend piu nuovo): si mostrano con la chiave come etichetta.
  for (const [k, v] of Object.entries(valori)) {
    if (!CAMPI_PREDEFINITI.some((d) => d.chiave === k)) righe.push({ chiave: k, etichetta: k, valore: v });
  }
  return righe;
};

/** Copia negli appunti; false se il browser non lo consente. */
export const copiaNegliAppunti = async (testo: string): Promise<boolean> => {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(testo);
      return true;
    }
  } catch {
    // permesso negato o contesto non sicuro
  }
  return false;
};

// ===== Impostazioni del canale (admin, «Campi della richiesta») =====

/** Lunghezza massima di un'etichetta rinominata (vuota = predefinita). */
export const MAX_ETICHETTA = 40;

/** Etichetta predefinita di una chiave (segnaposto del campo di rinomina). */
export const etichettaPredefinita = (chiave: string): string =>
  CAMPI_PREDEFINITI.find((d) => d.chiave === chiave)?.etichetta || chiave;

export interface CanaleConCampi {
  chiaveSistema?: string | null;
  campiRichiesta?: unknown;
  etichetteCampi?: Record<string, string | null | undefined> | null;
}

/**
 * Campi rinominabili di un canale: quelli di `campiRichiesta`; se il server
 * non li manda ma il canale è di sistema, i predefiniti. Canale normale: [].
 */
export const campiDelCanale = (g: CanaleConCampi | null | undefined): CampoRichiesta[] => {
  if (!g || !g.chiaveSistema) return [];
  const campi = campiRichiestaDi(g.campiRichiesta);
  return campi.length > 0 ? campi : CAMPI_PREDEFINITI;
};

/**
 * Valori iniziali dei campi di rinomina: `etichetteCampi` se il server la
 * manda, altrimenti l'etichetta attuale quando diversa dalla predefinita.
 */
export const etichetteIniziali = (g: CanaleConCampi, campi: CampoRichiesta[]): Record<string, string> => {
  const out: Record<string, string> = {};
  const salvate = g.etichetteCampi && typeof g.etichetteCampi === 'object' ? g.etichetteCampi : null;
  for (const c of campi) {
    if (salvate) out[c.chiave] = (salvate[c.chiave] || '').trim();
    else out[c.chiave] = c.etichetta !== etichettaPredefinita(c.chiave) ? c.etichetta : '';
  }
  return out;
};
